<template>
  <div
    class="float-ball"
    :class="{ collapsed: isCollapsed }"
    ref="floatBallRef"
    :style="ballStyle"
    @touchstart.passive="onTouchStart"
    @touchmove.passive="onTouchMove"
    @touchend.passive="onTouchEnd"
    @mousedown="onMouseDown"
    @mouseenter="onMouseEnter"
    @click="onClick"
  >
    <div class="float-ball-inner" :style="innerStyle">
      <slot>
        <img src="../assets/icon_handzone.png" alt="HZ" class="float-ball-text">
        <!-- <span class="float-ball-text">HZ</span> -->
      </slot>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';

export default {
  name: 'FloatBall',
  emits: ['click'],
  props: {
    storageKey: {
      type: String,
      default: 'floatBallPosition'
    }
  },
  setup(props, { emit }) {
    const floatBallRef = ref(null);
    
    // 从 localStorage 读取位置
    const getSavedPosition = () => {
      try {
        const saved = localStorage.getItem(props.storageKey);
        if (saved) {
          const pos = JSON.parse(saved);
          // 确保位置在屏幕内
          return {
            x: Math.max(0, Math.min(pos.x, window.innerWidth - 50)),
            y: Math.max(0, Math.min(pos.y, window.innerHeight - 50)),
          };
        }
      } catch (e) {
        console.error('读取悬浮球位置失败:', e);
      }
      return null;
    };
    
    // 保存位置到 localStorage
    const savePosition = () => {
      try {
        localStorage.setItem(props.storageKey, JSON.stringify({
          x: position.x,
          y: position.y,
        }));
      } catch (e) {
        console.error('保存悬浮球位置失败:', e);
      }
    };
    
    // 获取初始位置
    const savedPos = getSavedPosition();
    
    // 球的位置
    const position = reactive({
      x: savedPos ? savedPos.x : window.innerWidth - 60,
      y: savedPos ? savedPos.y : window.innerHeight * 0.6,
    });
    
    // 触摸开始位置
    const touchStart = reactive({
      x: 0,
      y: 0,
      ballX: 0,
      ballY: 0,
    });
    
    // 是否正在拖动
    const isDragging = ref(false);
    
    // 是否正在鼠标拖动
    const isMouseDragging = ref(false);
    
    // 球的大小
    const ballSize = 55;
    
    // 吸附边距
    const margin = 0;
    
    // 收缩状态
    const isCollapsed = ref(false);
    
    // 收缩定时器
    let collapseTimer = null;
    
    // 计算样式
    const ballStyle = computed(() => ({
      transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
      width: `${ballSize}px`,
      height: `${ballSize}px`,
      opacity: isCollapsed.value ? 0.7 : 1,
    }));
    
    // 计算内部元素的偏移（用于收缩效果）
    const innerStyle = computed(() => {
      if (!isCollapsed.value) return {};
      
      // 根据位置判断是左边缘还是右边缘
      const centerX = position.x + ballSize / 2;
      const isLeft = centerX < window.innerWidth / 2;
      
      return {
        transform: isLeft ? 'translateX(-50%)' : 'translateX(50%)',
      };
    });
    
    // 吸附到边缘
    const snapToEdge = () => {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      
      // 水平方向吸附
      const centerX = position.x + ballSize / 2;
      if (centerX < screenWidth / 2) {
        // 吸附到左边
        position.x = margin;
      } else {
        // 吸附到右边
        position.x = screenWidth - ballSize - margin;
      }
      
      // 垂直方向限制在屏幕内
      if (position.y < margin) {
        position.y = margin;
      } else if (position.y > screenHeight - ballSize - margin) {
        position.y = screenHeight - ballSize - margin;
      }
      
      // 保存位置
      savePosition();
      
      // 2秒后收缩
      startCollapseTimer();
    };
    
    // 开始收缩定时器
    const startCollapseTimer = () => {
      clearCollapseTimer();
      collapseTimer = setTimeout(() => {
        isCollapsed.value = true;
      }, 2000);
    };
    
    // 清除收缩定时器
    const clearCollapseTimer = () => {
      if (collapseTimer) {
        clearTimeout(collapseTimer);
        collapseTimer = null;
      }
    };
    
    // 展开球
    const expandBall = () => {
      isCollapsed.value = false;
      clearCollapseTimer();
    };
    
    // 触摸开始
    const onTouchStart = (e) => {
      expandBall();
      const touch = e.touches[0];
      touchStart.x = touch.clientX;
      touchStart.y = touch.clientY;
      touchStart.ballX = position.x;
      touchStart.ballY = position.y;
      isDragging.value = false;
    };
    
    // 触摸移动
    const onTouchMove = (e) => {
      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStart.x;
      const deltaY = touch.clientY - touchStart.y;
      
      // 移动超过5px认为是拖动
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        isDragging.value = true;
      }
      
      // 更新位置
      let newX = touchStart.ballX + deltaX;
      let newY = touchStart.ballY + deltaY;
      
      // 限制在屏幕内
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      
      newX = Math.max(0, Math.min(newX, screenWidth - ballSize));
      newY = Math.max(0, Math.min(newY, screenHeight - ballSize));
      
      position.x = newX;
      position.y = newY;
    };
    
    // 触摸结束
    const onTouchEnd = () => {
      snapToEdge();
    };
    
    // 鼠标按下
    const onMouseDown = (e) => {
      e.preventDefault();
      expandBall();
      isMouseDragging.value = true;
      touchStart.x = e.clientX;
      touchStart.y = e.clientY;
      touchStart.ballX = position.x;
      touchStart.ballY = position.y;
      isDragging.value = false;
      
      // 禁用页面上的 pointer-events，防止鼠标离开元素后丢失事件
      document.body.style.pointerEvents = 'none';
      floatBallRef.value.style.pointerEvents = 'auto';
      
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };
    
    // 鼠标移动
    const onMouseMove = (e) => {
      if (!isMouseDragging.value) return;
      
      const deltaX = e.clientX - touchStart.x;
      const deltaY = e.clientY - touchStart.y;
      
      // 移动超过5px认为是拖动
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        isDragging.value = true;
      }
      
      // 更新位置
      let newX = touchStart.ballX + deltaX;
      let newY = touchStart.ballY + deltaY;
      
      // 限制在屏幕内
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      
      newX = Math.max(0, Math.min(newX, screenWidth - ballSize));
      newY = Math.max(0, Math.min(newY, screenHeight - ballSize));
      
      position.x = newX;
      position.y = newY;
    };
    
    // 鼠标抬起
    const onMouseUp = () => {
      isMouseDragging.value = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      // 恢复页面 pointer-events
      document.body.style.pointerEvents = '';
      snapToEdge();
    };
    
    // 鼠标进入球时展开
    const onMouseEnter = () => {
      expandBall();
    };
    
    // 点击事件
    const onClick = (e) => {
      if (!isDragging.value) {
        emit('click', e);
      }
    };
    
    // 窗口大小改变时重新吸附
    const onResize = () => {
      snapToEdge();
    };
    
    onMounted(() => {
      snapToEdge();
      window.addEventListener('resize', onResize);
    });
    
    onUnmounted(() => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.pointerEvents = '';
      clearCollapseTimer();
    });
    
    return {
      floatBallRef,
      ballStyle,
      innerStyle,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onMouseDown,
      onMouseEnter,
      onClick,
    };
  },
};
</script>

<style scoped>
.float-ball {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 0;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  transition: transform 0.1s ease-out;
}

.float-ball-inner {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  /* box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4); */
  /* border: 2px solid rgba(255, 255, 255, 0.3); */
}

.float-ball-text {
  color: #fff;
  font-size: 12px;
  width: 100%;
  height: 100%;
  font-weight: bold;
  white-space: nowrap;
}

/* 收缩状态 */
.float-ball.collapsed {
  transition: transform 0.3s ease-out, opacity 0.3s ease-out;
}

.float-ball.collapsed .float-ball-inner {
  transform: scale(0.5);
}

/* 拖动时移除过渡效果，使跟随更流畅 */
.float-ball:active {
  transition: none;
}

.float-ball:active .float-ball-inner {
  transform: scale(0.95);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}
</style>
